// 4th form
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Upload,
  Trash2,
  X,
  Image as ImageIcon,
  Image,
  Loader2,
  ShieldAlert,
  Plus,
} from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import { LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import FileUpload from '@/js/components/FileUpload';
import { fetchExistingDocuments } from '@/js/components/FileUploadBox';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

const technicalDetailsSchema = z.object({
  capacity: z.number().min(1, 'Enter the Weight Capacity (tons/month)'),
  // industries_served: z.array(z.string()).min(1, 'Select at least one industry'),
  industries_served: z.array(z.string()),
  materials: z.array(z.string()).min(1, 'Enter at least one material'),
  manufacturing_processes: z
    .array(z.string())
    .min(1, 'Enter at least one manufacturing process you use'),
  // number_of_plants_facilities: z
  //   .number()
  //   .min(1, 'Enter the number of Plant/facilities'),
  // number_of_machinery: z.number().min(1, 'Enter the number of machines'),
});

const DOCUMENT_TITLES = {
  workSample_one: 'Work Sample 1',
  workSample_two: 'Work Sample 2',
  workSample_three: 'Work Sample 3',
  workSample_four: 'Work Sample 4',
};
const TechnicalDetailsScreen = ({ setSectionId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);

  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const token = useSelector((state) => state.authReducer.token);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [existingDocuments, setExistingDocuments] = useState([]);
  // const [processes, setProcesses] = useState(
  //   reduxData?.manufacturing_processes?.split(',') || [],
  // );
  // const [industries, setIndustries] = useState(
  //   reduxData?.industries_served?.split(',') || [],
  // );
  // const [materials, setMaterials] = useState(
  //   reduxData?.materials?.split(',') || [],
  // );
  const [processes, setProcesses] = useState(
    Array.isArray(reduxData?.manufacturing_processes)
      ? reduxData?.manufacturing_processes
      : reduxData?.manufacturing_processes?.split(',') || [],
  );
  const [industries, setIndustries] = useState(
    Array.isArray(reduxData?.industries_served)
      ? reduxData?.industries_served
      : reduxData?.industries_served?.split(',') || [],
  );
  const [materials, setMaterials] = useState(
    Array.isArray(reduxData?.materials)
      ? reduxData?.materials
      : reduxData?.materials?.split(',') || [],
  );

  const form = useForm({
    resolver: zodResolver(technicalDetailsSchema),
    defaultValues: {
      capacity: reduxData?.capacity ? Number(reduxData.capacity) : '',
      industries_served: reduxData?.industries_served ? industries : [],
      manufacturing_processes: reduxData?.technical_details
        ?.manufacturing_processes
        ? processes
        : [],
      materials: reduxData?.materials ? materials : [],
      // number_of_machinery: reduxData?.number_of_machinery
      //   ? Number(reduxData.number_of_machinery)
      //   : '',
      // number_of_plants_facilities: reduxData?.number_of_plants_facilities
      //   ? Number(reduxData.number_of_plants_facilities)
      //   : '',
    },
  });

  const [documentStates, setDocumentStates] = useState({
    workSample_one: null,
    workSample_two: null,
    workSample_three: null,
    workSample_four: null,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    workSample_one: 0,
    workSample_two: 0,
    workSample_three: 0,
    workSample_four: 0,
  });
  // Fetch existing documents on component mount
  useEffect(() => {
    fetchExistingDocuments(apiClient, setExistingDocuments, setError);
  }, [token]);

  const onSubmit = async (data) => {
    setProcessing(true);
    try {
      // Prepare technical details data
      const docIDs = existingDocuments.map((document) => document.id);

      const technicalDetailsData = {
        capacity: data.capacity,
        materials: materials,
        industries_served: industries,
        manufacturing_processes: processes,
        document_ids: docIDs,
      };

      // Submit technical details
      const technicalResponse = await apiClient.put(
        '/api/manufacturer-profile/technical-details',
        JSON.stringify(technicalDetailsData),
      );

      // Log submission data
      const submissionData = {
        ...data,
        industries_served: industries,
        manufacturing_processes: processes,
        materials: materials,
      };
      console.log('Technical Details:', submissionData);
      dispatch(registerSupplierAction(submissionData));

      // Move to next section
      setSectionId(4);
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      setError(error_message || 'Failed to update technical details');
      console.error('Error submitting technical details: ', error_message);
    } finally {
      setProcessing(false);
    }
  };

  // useferct
  useEffect(() => {
    form.setValue('industries_served', industries);
  }, [industries]);

  useEffect(() => {
    form.setValue('manufacturing_processes', processes);
  }, [processes]);

  useEffect(() => {
    form.setValue('materials', materials);
  }, [materials]);

  const handleTagInput = (e, setter, fieldName) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      e.preventDefault();
      const value = e.target.value.trim();
      setter((prev) => [...new Set([...prev, value])]);
      e.target.value = '';
    }
  };

  const removeTag = (index, setter) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const industriesInputRef = useRef(null);
  const materialsInputRef = useRef(null);
  const machineryInputRef = useRef(null);
  const processesInputRef = useRef(null);

  // ----------------------------------------------------------- //
  return (
    <div className="w-full bg-white  px-20  rounded-md font-roboto text-gray-700">
      <h2 className="text-3xl font-semibold text-black text-center mb-4">
        Technical Details
      </h2>
      <p className="text-center text-[#637381] mb-8 text-base">
        Please add specific information to impress our buyers with your capacity
        and capabilities
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* <FormField
            control={form.control}
            name="workSamples"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Upload Photos Of Your Work Samples
                </FormLabel>
                <FormControl>
                  <FileUploadBox
                    id="workSamples"
                    label="Add Photos"
                    files={field.value}
                    onChange={(files) => field.onChange(files)}
                    onRemove={(index) => {
                      const newFiles = [...field.value];
                      newFiles.splice(index, 1);
                      field.onChange(newFiles);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}
          <FormField
            control={form.control}
            name="capacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Weight Capacity{' '}
                  <span className="text-gray-500 ">( tons / month )</span>
                </FormLabel>
                <FormControl>
                  <div className="w-full">
                    <Input
                      type="number"
                      min="0"
                      placeholder="Enter monthly weight capacity eg. 390 (ton/month)"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) => {
                        const value =
                          e.target.value === '' ? '' : Number(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="industries_served"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Industries You Have Served
                </FormLabel>
                <FormControl>
                  <div className="w-full">
                    <div className="relative">
                      <Input
                        placeholder="Add industries (Press Enter to add)"
                        onKeyDown={(e) =>
                          handleTagInput(e, setIndustries, 'industries_served')
                        }
                        ref={industriesInputRef}
                        className="  focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      {/* Plus Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const input = industriesInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault: () => {},
                              },
                              setIndustries,
                              'industries_served',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {industries.map((industry, index) => (
                        <div
                          key={index}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {industry}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setIndustries,
                                'industries_served',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="materials"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Materials You Work With{' '}
                </FormLabel>
                <FormControl>
                  <div>
                    <div className="relative">
                      <Input
                        placeholder="Add materials (Press Enter to add)"
                        onKeyDown={(e) =>
                          handleTagInput(e, setMaterials, 'materials')
                        }
                        ref={materialsInputRef}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      {/* Plus Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const input = materialsInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault: () => {},
                              },
                              setMaterials,
                              'materials',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {materials.map((material, index) => (
                        <div
                          key={index}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {material}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(index, setMaterials, 'materials')
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="manufacturing_processes"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  List of Manufacturing Processes{' '}
                </FormLabel>
                <FormControl>
                  <div>
                    <div className="relative">
                      <Input
                        placeholder="Add manufacturing processes (Press Enter to add)"
                        onKeyDown={(e) =>
                          handleTagInput(
                            e,
                            setProcesses,
                            'manufacturing_processes',
                          )
                        }
                        ref={processesInputRef}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                      {/* Plus Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const input = processesInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault: () => {},
                              },
                              setProcesses,
                              'manufacturing_processes',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {processes.map((process, index) => (
                        <div
                          key={index}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {process}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(
                                index,
                                setProcesses,
                                'manufacturing_processes',
                              )
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          {/* <FormField
            control={form.control}
            name="machineryList"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  List of machinery
                </FormLabel>
                <FormControl>
                  <div>
                    <div className="relative">
                      <Input
                        placeholder="Add machinery (e.g. ABC40T 40 ton 5mm to 1000mm size, Press Enter to add)"
                        onKeyDown={(e) =>
                          handleTagInput(e, setMachinery, 'machineryList')
                        }
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                        ref={machineryInputRef}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = machineryInputRef.current;
                          if (input && input.value.trim() !== '') {
                            handleTagInput(
                              {
                                key: 'Enter',
                                target: input,
                                preventDefault: () => {},
                              },
                              setMachinery,
                              'machineryList',
                            );
                          }
                        }}
                        className="absolute inset-y-0 right-2 flex items-center justify-center text-lightPurple hover:text-darkerPurple transition"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {machinery.map((machine, index) => (
                        <div
                          key={index}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center"
                        >
                          {machine}
                          <button
                            type="button"
                            onClick={() =>
                              removeTag(index, setMachinery, 'machineryList')
                            }
                            className="ml-2"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}

          {/* <FormField
            control={form.control}
            name="number_of_plants_facilities"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Total Number of Plants/Facilities
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Cleanroom, Testing Lab, Assembly line etc"
                    {...field}
                    className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0  text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    onChange={(e) => {
                      const value =
                        e.target.value === '' ? '' : Number(e.target.value);
                      field.onChange(value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="number_of_machinery"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground font-semibold text-gray-700">
                  Total Number of Machines
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter the number of machines"
                    {...field}
                    className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0  text-black focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    onChange={(e) => {
                      const value =
                        e.target.value === '' ? '' : Number(e.target.value);
                      field.onChange(value);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> */}

          <div className=" flex flex-col gap-2 justify-between items-between">
            <div>
              <span className="text-foreground text-sm font-semibold text-gray-700">
                Upload Photos of Your Work Samples
              </span>
            </div>
            <div className="flex justify-between mb-4 ">
              {[
                {
                  name: 'Work Sample-1',
                  label: 'Upload Work Sample',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Work Sample-1',
                  ),
                },
                {
                  name: 'Work Sample-2',
                  label: 'Upload Work Sample',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Work Sample-2',
                  ),
                },
                {
                  name: 'Work Sample-3',
                  label: 'Upload Work Sample',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Work Sample-3',
                  ),
                },
                {
                  name: 'Work Sample-4',
                  label: 'Upload Work Sample',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Work Sample-4',
                  ),
                },
              ].map(({ name, label, existingKey }) => (
                <FileUpload
                  DOCUMENT_TITLES={DOCUMENT_TITLES}
                  MAX_FILE_SIZE={MAX_FILE_SIZE}
                  ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                  existingDocuments={existingDocuments}
                  setExistingDocuments={setExistingDocuments}
                  documentStates={documentStates}
                  setDocumentStates={setDocumentStates}
                  uploadProgress={uploadProgress}
                  setUploadProgress={setUploadProgress}
                  isUploading={isUploading}
                  setIsUploading={setIsUploading}
                  setError={setError}
                  key={name}
                  id={name}
                  label={label}
                  documentType={name}
                  existingDocument={existingKey}
                  classNames="w-40 h-28"
                />
              ))}
            </div>
          </div>

          <div className="flex justify-between mt-8 text-lg">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSectionId(2)}
              className="px-4 py-2 h-12 w-36 hover:bg-slate-200"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="px-4 py-2 h-12 w-36 bg-darkPurple hover:bg-darkerPurple ease-in duration-300"
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Next
                </span>
              )}
            </Button>
          </div>
          <div className="flex flex-row items-center justify-center space-x-2 ">
            {/* conditional renderign if no error display this if error dispaly error instead */}
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3 w-3" />
                <span className="text-xs text-[#2CD673]">
                  We use industry-standard encryption to protect your data
                </span>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};

export default TechnicalDetailsScreen;
